import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AuthPage } from '@/features/auth/AuthPage';
import { ErrorScreen } from '@/components/system/ErrorScreen';
import { NotFoundPage } from '@/components/system/NotFoundPage';
import { RequireAuth } from '@/components/system/RequireAuth';
import { RoleHome } from '@/components/system/RoleHome';
import { lazyEl } from '@/components/system/LazyPage';

// VLT-297: cada módulo llega en su propio chunk — el bundle inicial solo trae
// el cascarón + la portada del rol; el resto se carga al navegar (con skeleton).

// Module routes. Feature pages are introduced phase by phase (ROADMAP.md);
// until then each renders a premium placeholder so navigation never breaks.
// VLT-334 · ADR-017: la landing SOLO entra al bundle con el interruptor de
// build encendido (demo/E2E). En producción la condición es falsa en tiempo
// de compilación → tree-shaking la elimina por completo: cero divulgación
// pública hasta que counsel presente la provisional.
const landingRoutes =
  import.meta.env.VITE_LANDING_ENABLED === '1'
    ? [{
        path: '/landing',
        element: lazyEl(() => import('@/features/landing/LandingPage').then((m) => ({ default: m.LandingPage }))),
        errorElement: <ErrorScreen />,
      }]
    : [];

export const router = createBrowserRouter([
  ...landingRoutes,
  { path: '/login', element: <AuthPage />, errorElement: <ErrorScreen /> },
  // Pública: el enlace de recuperación del correo aterriza aquí sin sesión normal.
  {
    path: '/reset-password',
    element: lazyEl(() => import('@/features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))),
    errorElement: <ErrorScreen />,
  },
  // Pública (G12 f2): el mini-sitio de la firma — el visitante NO tiene sesión.
  {
    path: '/s/:slug',
    element: lazyEl(() => import('@/features/site/FirmSitePage').then((m) => ({ default: m.FirmSitePage }))),
    errorElement: <ErrorScreen />,
  },
  // Pública (VLT-102 · 0068): el ENLACE DE CARGA — el cliente sube sin cuenta.
  {
    path: '/u/:token',
    element: lazyEl(() => import('@/features/uploadLink/UploadLinkPage').then((m) => ({ default: m.UploadLinkPage }))),
    errorElement: <ErrorScreen />,
  },
  // Pública (0070): el cliente sigue su TURNO de oficina sin cuenta.
  {
    path: '/t/:token',
    element: lazyEl(() => import('@/features/queue/TicketStatusPage').then((m) => ({ default: m.TicketStatusPage }))),
    errorElement: <ErrorScreen />,
  },
  {
    path: '/',
    element: <RequireAuth><AppShell /></RequireAuth>,
    errorElement: <ErrorScreen />,
    children: [
      { index: true, element: <RoleHome /> },
      { path: 'tasks', element: lazyEl(() => import('@/features/tasks/TasksPage').then((m) => ({ default: m.TasksPage }))) },
      { path: 'clients', element: lazyEl(() => import('@/features/clients/ClientsPage').then((m) => ({ default: m.ClientsPage }))) },
      { path: 'clients/:id', element: lazyEl(() => import('@/features/clients/ClientProfilePage').then((m) => ({ default: m.ClientProfilePage }))) },
      { path: 'documents', element: lazyEl(() => import('@/features/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage }))) },
      { path: 'tax-returns', element: lazyEl(() => import('@/features/returns/TaxReturnsPage').then((m) => ({ default: m.TaxReturnsPage }))) },
      { path: 'tax-returns/:id', element: lazyEl(() => import('@/features/returns/ReturnWorkspacePage').then((m) => ({ default: m.ReturnWorkspacePage }))) },
      { path: 'portal', element: lazyEl(() => import('@/features/portal/ClientPortalPage').then((m) => ({ default: m.ClientPortalPage }))) },
      { path: 'queue', element: lazyEl(() => import('@/features/queue/QueuePage').then((m) => ({ default: m.QueuePage }))) },
      { path: 'appointments', element: lazyEl(() => import('@/features/appointments/AppointmentsPage').then((m) => ({ default: m.AppointmentsPage }))) },
      { path: 'notarization', element: lazyEl(() => import('@/features/notarization/NotarizationPage').then((m) => ({ default: m.NotarizationPage }))) },
      { path: 'payments', element: lazyEl(() => import('@/features/payments/PaymentsPage').then((m) => ({ default: m.PaymentsPage }))) },
      { path: 'subscriptions', element: lazyEl(() => import('@/features/subscriptions/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage }))) },
      { path: 'billing', element: lazyEl(() => import('@/features/billing/BillingCenterPage').then((m) => ({ default: m.BillingCenterPage }))) },
      { path: 'downloads', element: lazyEl(() => import('@/features/downloads/DownloadsPage').then((m) => ({ default: m.DownloadsPage }))) },
      { path: 'assistant', element: lazyEl(() => import('@/features/assistant/AssistantPage').then((m) => ({ default: m.AssistantPage }))) },
      { path: 'reports', element: lazyEl(() => import('@/features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage }))) },
      { path: 'audit', element: lazyEl(() => import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage }))) },
      { path: 'messages', element: lazyEl(() => import('@/features/messages/MessagesPage').then((m) => ({ default: m.MessagesPage }))) },
      { path: 'crm', element: lazyEl(() => import('@/features/crm/CrmPage').then((m) => ({ default: m.CrmPage }))) },
      { path: 'support', element: lazyEl(() => import('@/features/support/SupportPage').then((m) => ({ default: m.SupportPage }))) },
      { path: 'team', element: lazyEl(() => import('@/features/team/TeamPage').then((m) => ({ default: m.TeamPage }))) },
      { path: 'security', element: lazyEl(() => import('@/features/security/SecurityCompliancePage').then((m) => ({ default: m.SecurityCompliancePage }))) },
      { path: 'mfa', element: lazyEl(() => import('@/features/account/MfaPage').then((m) => ({ default: m.MfaPage }))) },
      { path: 'sandbox', element: lazyEl(() => import('@/features/sandbox/SandboxPage').then((m) => ({ default: m.SandboxPage }))) },
      { path: 'settings', element: lazyEl(() => import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))) },
      // 404 propia: una ruta que no existe no es un «incidente».
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
